import {
  type ConsoleConversationSummary,
  type ConsoleConversationTrace,
  type ConsoleToolHealth,
  ConversationIdSchema,
  MessageIdSchema,
  RunIdSchema,
  UserIdSchema,
} from '@plato/shared';
import type { ConsoleData } from './console-data.ts';

const ORDERS = ConversationIdSchema.parse('conv_demoOrders');
const REFUNDS = ConversationIdSchema.parse('conv_demoRefunds');
const ALEX = UserIdSchema.parse('usr_demoAlex');
const JO = UserIdSchema.parse('usr_demoJo');
const QUESTION = MessageIdSchema.parse('msg_demoQuestion');
const REPLY = MessageIdSchema.parse('msg_demoReply');
const RUN = RunIdSchema.parse('run_demoRun');

const SUMMARIES: readonly ConsoleConversationSummary[] = [
  {
    conversation_id: ORDERS,
    user_id: ALEX,
    user_subject: 'example-user-1',
    organization: 'example-client-a',
    title: 'Revenue by category',
    purpose: 'chat',
    created_at: '2026-10-16T08:58:00.000Z',
    updated_at: '2026-10-16T09:02:00.000Z',
    runs: 1,
    failed_runs: 0,
    tool_calls: 2,
    failed_tool_calls: 1,
    thumbs_up: 0,
    thumbs_down: 1,
  },
  {
    conversation_id: REFUNDS,
    user_id: JO,
    user_subject: 'example-user-2',
    organization: 'example-client-b',
    title: 'Open refunds this week',
    purpose: 'scheduled',
    created_at: '2026-10-15T14:10:00.000Z',
    updated_at: '2026-10-15T14:12:00.000Z',
    runs: 2,
    failed_runs: 0,
    tool_calls: 1,
    failed_tool_calls: 0,
    thumbs_up: 1,
    thumbs_down: 0,
  },
];

const TRACE: ConsoleConversationTrace = {
  conversation: {
    id: ORDERS,
    user_id: ALEX,
    title: 'Revenue by category',
    purpose: 'chat',
    created_at: '2026-10-16T08:58:00.000Z',
    updated_at: '2026-10-16T09:02:00.000Z',
  },
  messages: [
    {
      id: QUESTION,
      role: 'user',
      run_id: RUN,
      position: 0,
      created_at: '2026-10-16T08:58:00.000Z',
      model_id: null,
      parts: [{ type: 'text', text: 'Chart revenue by category for September.' }],
    },
    {
      id: REPLY,
      role: 'assistant',
      run_id: RUN,
      position: 1,
      created_at: '2026-10-16T09:02:00.000Z',
      model_id: 'example-model-fast',
      parts: [
        {
          type: 'tool-list_orders',
          toolCallId: 'call_1',
          state: 'output-error',
          input: { month: '2026-09' },
          errorText: 'The orders service did not answer in time.',
        },
        {
          type: 'tool-bash',
          toolCallId: 'call_2',
          state: 'output-available',
          input: { command: 'python chart.py' },
          output: { stdout: 'wrote chart.json' },
        },
        { type: 'text', text: 'Here is September revenue by category, from the cached export.' },
      ],
    },
  ],
  runs: [
    {
      id: RUN,
      status: 'completed',
      stop_reason: 'end_turn',
      model_id: 'example-model-fast',
      model_route: 'example',
      purpose: 'chat',
      started_at: '2026-10-16T08:58:01.000Z',
      ended_at: '2026-10-16T09:02:00.000Z',
      usage: {
        inputTokens: 18_200,
        outputTokens: 640,
        costMicroUsd: 61_000,
        creditMicros: 122_000,
      },
      system_prompt: [
        { role: 'system', content: 'Example platform rules. The real prompt appears here.' },
        {
          role: 'system',
          content:
            '<product>Example Software Co</product>\n<vendor_instructions>Answer in British English.</vendor_instructions>',
        },
      ],
      tools: {
        toolStyle: 'function-tools',
        tools: [
          {
            name: 'list_orders',
            type: 'function',
            description: 'List the signed-in user’s orders for a month.',
            inputSchema: { type: 'object', properties: { month: { type: 'string' } } },
          },
          {
            name: 'bash',
            type: 'function',
            description: 'Run a command in the sandbox.',
            inputSchema: { type: 'object', properties: { command: { type: 'string' } } },
          },
        ],
      },
      messages: [{ role: 'user', content: 'Chart revenue by category for September.' }],
      reply_message_id: REPLY,
      tool_calls: [
        {
          tool_call_id: 'call_1',
          tool_name: 'list_orders',
          tool_kind: 'mcp',
          mcp_server: 'orders',
          mcp_tool_name: 'list_orders',
          arguments: { month: '2026-09' },
          started_at: '2026-10-16T08:58:03.000Z',
          status: 'timeout',
          error_type: 'timeout',
          duration_ms: 30_000,
          result_bytes: null,
        },
        {
          tool_call_id: 'call_2',
          tool_name: 'bash',
          tool_kind: 'sandbox',
          mcp_server: null,
          mcp_tool_name: null,
          arguments: { command: 'python chart.py' },
          started_at: '2026-10-16T09:01:20.000Z',
          status: 'ok',
          error_type: null,
          duration_ms: 2_300,
          result_bytes: 412,
        },
      ],
    },
  ],
  feedback: [
    {
      message_id: REPLY,
      rating: 'down',
      comment: 'It used last month’s export.',
      updated_at: '2026-10-16T09:03:00.000Z',
    },
  ],
};

const TOOL_HEALTH: ConsoleToolHealth = {
  from: '2026-10-02T00:00:00.000Z',
  to: '2026-10-16T00:00:00.000Z',
  data: [
    {
      day: '2026-10-15',
      tool_name: 'list_orders',
      tool_kind: 'mcp',
      mcp_server: 'orders',
      calls: 120,
      failures: 18,
      p50_ms: 900,
      p95_ms: 30_000,
    },
    {
      day: '2026-10-15',
      tool_name: 'bash',
      tool_kind: 'sandbox',
      mcp_server: null,
      calls: 86,
      failures: 1,
      p50_ms: 1_400,
      p95_ms: 6_200,
    },
  ],
};

export const demoConversations: Pick<
  ConsoleData,
  'listConversations' | 'getConversationTrace' | 'saveReplyAsEvalCase' | 'getToolHealth'
> = {
  listConversations: async (_tenant, filter) => {
    const data = SUMMARIES.filter(
      (row) =>
        (filter.rating === undefined ||
          (filter.rating === 'up' && row.thumbs_up > 0) ||
          (filter.rating === 'down' && row.thumbs_down > 0) ||
          (filter.rating === 'none' && row.thumbs_up + row.thumbs_down === 0)) &&
        (filter.organization === undefined || row.organization === filter.organization),
    );
    return { data, total: data.length, next_cursor: null };
  },
  getConversationTrace: async () => TRACE,
  saveReplyAsEvalCase: async () => 'evc_demoSaved',
  getToolHealth: async () => TOOL_HEALTH,
};
